import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * Text shown inside the popup. Ignored when default-slotted content is supplied via the content slot.
   *
   * @type {String}
   * @default undefined
   */
  content: {
    type: String,
    default: undefined,
  },
  /**
   * Preferred placement of the popup relative to the activator. The component automatically falls back to a perpendicular position when the preferred placement does not fit in the viewport. "top" floats above, "bottom" below, "left" to the start side, "right" to the end side.
   *
   * @type {String}
   * @default "bottom"
   * @values top, bottom, left, right
   * @storybook-control select
   */
  position: {
    type: String,
    default: "bottom",
    validator: (val) => ["top", "bottom", "left", "right"].includes(val),
  },
  /**
   * Suppress the popup entirely. The activator still renders but no tooltip appears on hover or focus.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Show the popup only while the viewport is at most this many pixels wide. 0 shows it at every width.
   *
   * @type {Number|String}
   * @default 0
   * @storybook-control number
   */
  maxViewportWidth: {
    type: [Number, String],
    default: 0,
  },
  /**
   * Keep popup content on a single line and truncate with ellipsis when it overflows the max-width. Useful for short labels where wrapping would look ragged.
   *
   * @type {Boolean|String}
   * @default false
   */
  noWrap: booleanAttribute(),
  /**
   * Leave the activator out of the tab order, for an activator that wraps its own focusable control. Focus on that control still opens the popup.
   * The popup stays described on the activator wrapper, so the wrapped control carries the tooltip text itself, for example as its `aria-description`.
   *
   * @type {Boolean|String}
   * @default false
   */
  noActivatorFocus: booleanAttribute(),
  /**
   * CSS cursor applied to the activator slot.
   *
   * @type {String}
   * @default "pointer"
   */
  activatorCursor: {
    type: String,
    default: "pointer",
  },
  /**
   * Gap in pixels between the activator and the popup edge.
   *
   * @type {Number|String}
   * @default 12
   * @storybook-control number
   */
  popupSpacing: {
    type: [Number, String],
    default: 12,
  },
  /**
   * Colour theme. "light" pairs with the surface tokens; "dark" inverts to a dark popup with light text.
   *
   * @type {String}
   * @default "light"
   * @values light, dark
   * @storybook-control select
   */
  theme: {
    type: String,
    default: "light",
    validator: (val) => ["light", "dark"].includes(val),
  },
  /**
   * Delay in milliseconds before the popup opens on hover or focus.
   *
   * @type {Number|String}
   * @default 0
   * @storybook-control number
   */
  openDelay: {
    type: [Number, String],
    default: 0,
  },
  /**
   * Delay in milliseconds before the popup closes after the pointer leaves both the activator and the popup. The grace period lets the user move the pointer into the popup itself.
   *
   * @type {Number|String}
   * @default 200
   * @storybook-control number
   */
  closeDelay: {
    type: [Number, String],
    default: 200,
  },
});
